import { useNavigate } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import { OPERATIONS, analyzeSql, type SqlLibraryItemDto } from '@visualitics-toolkit/shared';

import { Badge, Button, EmptyState, Field, FileDrop, Input, Textarea } from '../components/atoms';
import { ErrorBlock, LoadingBlock, formatDateTime } from '../components/molecules';
import { Dialog } from '../components/organisms/Dialog';
import { useDeleteSqlLibraryItem, useSaveSqlLibraryItem, useSqlLibrary } from '../lib/queries';

/** As rotas de operação nascem do registry, então o destino é dado, não literal de tipo. */
const SQL_RUN_ROUTE: string = OPERATIONS['sql.run-library'].route ?? '/ops/sql-library-run';

export function SqlLibraryPage(): ReactNode {
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<SqlLibraryItemDto | 'new' | null>(null);
  const library = useSqlLibrary(search || undefined);
  const remove = useDeleteSqlLibraryItem();
  const navigate = useNavigate();

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <h1 className="text-lg font-semibold text-content">Biblioteca SQL</h1>
        <div className="flex gap-2">
          <Input
            placeholder="Buscar por título ou descrição…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <Button variant="primary" onClick={() => setEditing('new')}>
            Novo item
          </Button>
        </div>
      </header>

      {library.isLoading && <LoadingBlock />}
      {library.data?.length === 0 && (
        <EmptyState
          title="Biblioteca vazia"
          description="Salve consultas recorrentes aqui para executá-las com preflight e auditoria."
        />
      )}

      <ul className="space-y-2">
        {library.data?.map((item) => (
          <li key={item.id} className="card space-y-2">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <p className="flex items-center gap-2 text-sm font-medium text-content">
                  {item.title}
                  <Badge tone={item.readOnly ? 'accent' : 'warning'}>
                    {item.readOnly ? 'somente leitura' : 'contém escrita'}
                  </Badge>
                  {item.tags.map((tag) => (
                    <Badge key={tag}>{tag}</Badge>
                  ))}
                </p>
                {item.description && <p className="text-sm text-muted">{item.description}</p>}
                <p className="text-xs text-muted">atualizado em {formatDateTime(item.updatedAt)}</p>
              </div>
              <div className="flex gap-1">
                <Button
                  onClick={() =>
                    void navigate({
                      to: SQL_RUN_ROUTE,
                      search: { sqlLibraryItemId: item.id },
                    })
                  }
                >
                  Executar
                </Button>
                <Button onClick={() => setEditing(item)}>Editar</Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    if (confirm(`Remover "${item.title}" da biblioteca?`)) remove.mutate(item.id);
                  }}
                >
                  Remover
                </Button>
              </div>
            </div>
            <pre className="max-h-32 overflow-auto rounded-md border border-border bg-surface px-3 py-2 font-mono text-xs text-muted">
              {item.sqlBody}
            </pre>
          </li>
        ))}
      </ul>

      {editing && (
        <SqlLibraryDialog
          item={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function SqlLibraryDialog({
  item,
  onClose,
}: {
  item: SqlLibraryItemDto | null;
  onClose: () => void;
}): ReactNode {
  const [title, setTitle] = useState(item?.title ?? '');
  const [description, setDescription] = useState(item?.description ?? '');
  const [sqlBody, setSqlBody] = useState(item?.sqlBody ?? '');
  const [tags, setTags] = useState(item?.tags.join(', ') ?? '');
  const save = useSaveSqlLibraryItem();

  // O veredito de leitura/escrita é mostrado ANTES de salvar; o servidor recalcula ao gravar.
  const analysis = sqlBody.trim() ? analyzeSql(sqlBody) : null;

  return (
    <Dialog title={item ? 'Editar item' : 'Novo item da biblioteca'} onClose={onClose} wide>
      <Field label="Título *">
        <Input value={title} onChange={(event) => setTitle(event.target.value)} />
      </Field>
      <Field label="Descrição">
        <Input value={description} onChange={(event) => setDescription(event.target.value)} />
      </Field>
      <Field label="Tags" help="Separadas por vírgula.">
        <Input value={tags} onChange={(event) => setTags(event.target.value)} />
      </Field>
      <Field label="SQL *">
        <Textarea rows={12} value={sqlBody} onChange={(event) => setSqlBody(event.target.value)} />
      </Field>

      <FileDrop accept=".sql,text/plain" label="Importar arquivo .sql" onFile={setSqlBody} />

      {analysis && (
        <p className="mt-3 flex flex-wrap items-center gap-2 text-sm">
          <Badge tone={analysis.isReadOnly ? 'accent' : 'warning'}>
            {analysis.isReadOnly ? 'somente leitura' : 'contém escrita'}
          </Badge>
          <span className="text-muted">
            {analysis.statementCount} statement(s)
            {analysis.writeKeywords.length > 0 && ` · ${analysis.writeKeywords.join(', ')}`}
          </span>
        </p>
      )}

      {save.error instanceof Error && <ErrorBlock className="mt-3" message={save.error.message} />}

      <div className="mt-4 flex justify-end gap-2">
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          variant="primary"
          loading={save.isPending}
          disabled={!title.trim() || !sqlBody.trim()}
          onClick={() =>
            save.mutate(
              {
                id: item?.id,
                values: {
                  title,
                  description: description || undefined,
                  sqlBody,
                  tags: tags
                    .split(',')
                    .map((tag) => tag.trim())
                    .filter(Boolean),
                },
              },
              { onSuccess: onClose },
            )
          }
        >
          Salvar
        </Button>
      </div>
    </Dialog>
  );
}
